type TDateLabelProps = {
  children: string;
  short?: boolean;
  withTime?: boolean;
  timeZone?: Intl.DateTimeFormatOptions['timeZone'];
};

/**
 * DateLabel component for formatting date strings.
 *
 * @param {TDateLabelProps} props - The component props.
 * @param {string | number | Date} props.children - The date value to be formatted. Can be a string, number, or Date object.
 * @param {boolean} [props.short=false] - Whether to use a short date format (YYYY-MM-DD).
 * @param {boolean} [props.withTime=false] - Whether to include the time in the formatted output.
 * @param {string} [props.timeZone] - Timezone for both date and time; defaults to the browser timezone.
 *
 * @returns {string} The formatted date string.
 */
const DateLabel = ({ children, short = false, withTime = false, timeZone }: TDateLabelProps): string => {
  // Parse the input date into a JavaScript Date object
  const date = new Date(children);

  // Check for an invalid date
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date provided to DateLabel');
  }

  let formattedDate;

  // Format the date based on the 'short' flag
  if (short) {
    formattedDate = date.toLocaleDateString('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone,
    }); // e.g., "2025-01-19"
  } else {
    formattedDate = date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      timeZone,
    }); // e.g., "19 January 2025"
  }

  // Append time if the 'withTime' flag is set
  if (withTime) {
    formattedDate += ` ${date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    })}`; // e.g., "19 January 2025 12:34"
  }

  return formattedDate;
};

export { DateLabel };
